import React from "react";
import { cn } from "@/lib/utils";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

interface DocumentationSidebarProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
}

const DocumentationSidebar = ({ activeTab, setActiveTab }: DocumentationSidebarProps) => {
  const handleSectionClick = (sectionId: string, tabValue: string) => {
    // Only switch tabs if needed
    if (activeTab !== tabValue) {
      setActiveTab(tabValue);
    }
    
    // Use short timeout to allow tab content to render before scrolling
    setTimeout(() => {
      window.location.hash = sectionId;
    }, 10);
  };

  return (
    <nav className="sticky top-20 max-h-[calc(100vh-80px)] overflow-y-auto pr-2">
      <div className="space-y-4">
        <div className="font-medium">On this page</div>
        
        <Accordion type="multiple" defaultValue={["getting-started", "leads", "companies", "users", "changelog"]}>
          <AccordionItem value="getting-started">
            <AccordionTrigger>Getting Started</AccordionTrigger>
            <AccordionContent>
              <div className="flex flex-col space-y-1">
                <a 
                  href="#overview" 
                  className="text-sm text-muted-foreground hover:text-foreground"
                  onClick={(e) => {
                    e.preventDefault();
                    handleSectionClick('overview', 'getting-started');
                  }}
                >
                  Application Overview
                </a>
                <a 
                  href="#navigation" 
                  className="text-sm text-muted-foreground hover:text-foreground"
                  onClick={(e) => {
                    e.preventDefault();
                    handleSectionClick('navigation', 'getting-started');
                  }}
                >
                  Navigation
                </a>
                <a 
                  href="#authentication" 
                  className="text-sm text-muted-foreground hover:text-foreground"
                  onClick={(e) => {
                    e.preventDefault();
                    handleSectionClick('authentication', 'getting-started');
                  }}
                >
                  Authentication
                </a>
                <a 
                  href="#dashboard" 
                  className="text-sm text-muted-foreground hover:text-foreground"
                  onClick={(e) => {
                    e.preventDefault();
                    handleSectionClick('dashboard', 'getting-started');
                  }}
                >
                  Dashboard
                </a>
              </div>
            </AccordionContent>
          </AccordionItem>
          
          <AccordionItem value="leads">
            <AccordionTrigger>Lead Management</AccordionTrigger>
            <AccordionContent>
              <div className="flex flex-col space-y-1">
                <a 
                  href="#leads-overview" 
                  className="text-sm text-muted-foreground hover:text-foreground"
                  onClick={(e) => {
                    e.preventDefault();
                    handleSectionClick('leads-overview', 'leads');
                  }}
                >
                  Overview
                </a>
                <a 
                  href="#adding-leads" 
                  className="text-sm text-muted-foreground hover:text-foreground"
                  onClick={(e) => {
                    e.preventDefault();
                    handleSectionClick('adding-leads', 'leads');
                  }}
                >
                  Adding Leads
                </a>
                <a 
                  href="#lead-details" 
                  className="text-sm text-muted-foreground hover:text-foreground"
                  onClick={(e) => {
                    e.preventDefault();
                    handleSectionClick('lead-details', 'leads');
                  }}
                >
                  Lead Details
                </a>
                <a 
                  href="#lead-scoring" 
                  className="text-sm text-muted-foreground hover:text-foreground"
                  onClick={(e) => {
                    e.preventDefault();
                    handleSectionClick('lead-scoring', 'leads');
                  }}
                >
                  Lead Scoring
                </a>
                <a 
                  href="#lead-activities" 
                  className="text-sm text-muted-foreground hover:text-foreground"
                  onClick={(e) => {
                    e.preventDefault();
                    handleSectionClick('lead-activities', 'leads');
                  }}
                >
                  Activities
                </a>
              </div>
            </AccordionContent>
          </AccordionItem>
          
          <AccordionItem value="companies">
            <AccordionTrigger>Company Management</AccordionTrigger>
            <AccordionContent>
              <div className="flex flex-col space-y-1">
                <a 
                  href="#companies-overview" 
                  className="text-sm text-muted-foreground hover:text-foreground"
                  onClick={(e) => {
                    e.preventDefault();
                    handleSectionClick('companies-overview', 'companies');
                  }}
                >
                  Overview
                </a>
                <a 
                  href="#creating-company" 
                  className="text-sm text-muted-foreground hover:text-foreground"
                  onClick={(e) => {
                    e.preventDefault();
                    handleSectionClick('creating-company', 'companies');
                  }}
                >
                  Creating a Company
                </a>
                <a 
                  href="#company-details" 
                  className="text-sm text-muted-foreground hover:text-foreground"
                  onClick={(e) => {
                    e.preventDefault();
                    handleSectionClick('company-details', 'companies');
                  }}
                >
                  Company Details
                </a>
                <a 
                  href="#company-members" 
                  className="text-sm text-muted-foreground hover:text-foreground"
                  onClick={(e) => {
                    e.preventDefault();
                    handleSectionClick('company-members', 'companies');
                  }}
                >
                  Managing Members
                </a>
                <a 
                  href="#company-switching" 
                  className="text-sm text-muted-foreground hover:text-foreground"
                  onClick={(e) => {
                    e.preventDefault();
                    handleSectionClick('company-switching', 'companies');
                  }}
                >
                  Company Switching
                </a>
                <a 
                  href="#company-web-search" 
                  className="text-sm text-muted-foreground hover:text-foreground"
                  onClick={(e) => {
                    e.preventDefault();
                    handleSectionClick('company-web-search', 'companies');
                  }}
                >
                  Web Search Enrichment
                </a>
                <a 
                  href="#company-access-requests" 
                  className="text-sm text-muted-foreground hover:text-foreground"
                  onClick={(e) => {
                    e.preventDefault();
                    handleSectionClick('company-access-requests', 'companies');
                  }}
                >
                  Access Requests
                </a>
              </div>
            </AccordionContent>
          </AccordionItem>
          
          <AccordionItem value="users">
            <AccordionTrigger>User Management</AccordionTrigger>
            <AccordionContent>
              <div className="flex flex-col space-y-1">
                <a 
                  href="#user-roles" 
                  className="text-sm text-muted-foreground hover:text-foreground"
                  onClick={(e) => {
                    e.preventDefault();
                    handleSectionClick('user-roles', 'users');
                  }}
                >
                  User Roles
                </a>
                <a 
                  href="#profile-settings" 
                  className="text-sm text-muted-foreground hover:text-foreground"
                  onClick={(e) => {
                    e.preventDefault();
                    handleSectionClick('profile-settings', 'users');
                  }}
                >
                  Profile Settings
                </a>
                <a 
                  href="#subscription-plans" 
                  className="text-sm text-muted-foreground hover:text-foreground"
                  onClick={(e) => {
                    e.preventDefault();
                    handleSectionClick('subscription-plans', 'users');
                  }}
                >
                  Subscription Plans
                </a>
                <a 
                  href="#sendgrid-setup" 
                  className="text-sm text-muted-foreground hover:text-foreground"
                  onClick={(e) => {
                    e.preventDefault();
                    handleSectionClick('sendgrid-setup', 'users');
                  }}
                >
                  SendGrid Email Setup
                </a>
              </div>
            </AccordionContent>
          </AccordionItem>
          
          <AccordionItem value="changelog">
            <AccordionTrigger>Changelog</AccordionTrigger>
            <AccordionContent>
              <div className="flex flex-col space-y-1">
                <a 
                  href="#recent-changes" 
                  className="text-sm text-muted-foreground hover:text-foreground"
                  onClick={(e) => {
                    e.preventDefault();
                    handleSectionClick('recent-changes', 'changelog');
                  }}
                >
                  Recent Changes
                </a>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </nav>
  );
};

export default DocumentationSidebar;
